import { yupResolver } from "@hookform/resolvers/yup";
import { CheckCircleRounded, EditSquare, FeedbackOutlined, Person2, Person2Outlined, Warning } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    Skeleton,
    Slider,
    styled,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import api from "../../api/client";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { getLabelByKey, useChangeableItems } from "../../store/useDynamicLabel";
import { formatDate } from "utils/formatDate";
import { AlertCircle, ArrowUpRightSquareIcon, ArrowUpSquareIcon, BotIcon, Building2, CalendarCheck2, ChartArea, CheckIcon, CircleCheckIcon, FileEditIcon, LucideArrowUpRightFromSquare, LucideSparkles, MessageCircleCheckIcon, NotepadText, Play, PlusIcon, Save, SparkleIcon, TimerIcon, UploadCloudIcon, UploadIcon, User } from "lucide-react";
import "./interviewFeedback.css"
import dayjs from "dayjs";
import CallOffInterviewDialog from "custom-components/interviewer/CallOffInterviewDialog";
import FeedbackRemarksDialog from "custom-components/interviewer/FeedbackRemarksDialog";

const InterviewFeedback = () => {
    const location = useLocation()
    const navigate = useNavigate()

    const { interviewId, interviewStatus } = location?.state || {}

    const { showSnackbar } = useSnackbar();

    const [skills, setSkills] = useState([]);
    const [interviewDetails, setInterviewDetails] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeURL, setResumeURL] = useState("")
    const [isFileUploading, setIsFileUploading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [aiSkillScoresAnalysis, setAiSkillScoresAnalysis] = useState({})
    const [isInterviewSkillsExtracting, setIsInterviewSkillsExtracting] = useState(false)
    const [aiEvaluatedDatas, setAiEvaluatedDatas] = useState({})
    const [openRemarksDialog, setOpenRemarksDialog] = useState(false)

    const [isEditingRecommendation, setIsEditingRecommendation] = useState(false);
    const [isEditingSkills, setIsEditingSkills] = useState(false);
    const [isEditingStrengths, setIsEditingStrengths] = useState(false)
    const [isEditingWeaknesses, setIsEditingWeaknesses] = useState(false)
    const [isEditingEvidences, setIsEditingEvidences] = useState(false)
    const [isEditingSummary, setIsEditingSummary] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const isInterviewAlreadyCompletedOrCancelled =
        interviewStatus?.toLowerCase() === 'completed' ||
        interviewStatus?.toLowerCase() === 'cancelled' ||
        interviewStatus?.toLowerCase() === 'rejected'

    const getFeedbackById = async () => {
        try {
            const response = await api.post("ViewFeedbackById", {
                interview_id: interviewId
            })
            setAiEvaluatedDatas(response?.data?.data?.evaluation || {})
            setSkills(response?.data?.data?.evaluation?.skill_scores || [])

            if (!response?.data?.data?.evaluation?.skill_scores?.length) {
                getSkills();
            }
        } catch (err) {
            console.error("error", err)
            showSnackbar("error", "Failed to get feedback")
        }
    }

    useEffect(() => {
        getFeedbackById()
    }, [])

    const getSkills = async () => {
        setIsInterviewSkillsExtracting(true)

        try {
            const response = await api.post("ExtractInterviewSkills", {
                interview_id: interviewId,
            });

            const requiredSkills =
                response?.data?.data?.required_skills || [];

            setSkills(
                response?.data?.data?.required_skills?.map((skill) => ({
                    skill_name: skill,
                    score: null,
                    feedback: "",
                }))
            )
        } catch (error) {
            console.error("Error fetching skills:", error);
        } finally {
            setIsInterviewSkillsExtracting(false)
        }
    };

    const getInterviewDetails = async () => {
        try {
            const response = await api.post("ViewInterviewByID", {
                interview_id: interviewId,
            });

            setInterviewDetails(response?.data?.data || null);
        } catch (error) {
            console.error("Error fetching interview details:", error);
        }
    };

    useEffect(() => {
        getInterviewDetails()
    }, [])

    const handleRunAnalysis = async () => {
        if (!resumeFile) {
            showSnackbar("error", "Please upload the transcript file")
            return;
        }
        setIsFileUploading(true)

        const formData = new FormData();

        formData.append("interview_id", interviewId)
        formData.append("transcript", resumeFile);

        try {
            const response = await api.post(
                "UploadInterviewTranscript",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response?.data?.status === "success") {
                showSnackbar("success", response?.data?.message);
                setResumeURL(response?.data?.data?.resume_url || "")
                setSkills((prev) => {
                    return prev?.map((skill) => {
                        const evaluation = response?.data?.data?.evaluation?.skill_scores?.find(
                            (item) => item?.skill_name === skill?.skill_name
                        )
                        return {
                            ...skill,
                            score: evaluation?.score ?? null,
                            feedback: evaluation?.feedback ?? "",
                        }
                    })
                })
                setAiEvaluatedDatas(response?.data?.data?.evaluation || {})
            } else {
                showSnackbar("error", "Unable to upload the transcript file");
            }
        } catch (error) {
            console.error("transcript file upload failed:", error);
            showSnackbar("error", "Failed to upload the transcript file");
            setResumeFile(null)
        } finally {
            setIsFileUploading(false)
        }
    };

    const handleSubmitFeedback = async (remarks) => {
        if (!remarks) {
            showSnackbar("error", "Please provide comments prior to submission")
            return;
        }
        setLoading(true)

        try {
            const payload = {
                interview_id: interviewId,
                reviewed_by: resumeFile ? "AI" : "INTERVIEWER",
                remarks: remarks,
                evaluation: {
                    ...aiEvaluatedDatas,
                    skill_scores: skills
                        ?.filter((item) => item?.skill_name?.trim())
                        ?.map((item) => ({
                            skill_name: item?.skill_name?.trim(),
                            score: item?.score,
                            feedback: item?.feedback
                        })),
                    strengths: aiEvaluatedDatas?.strengths?.filter((item) => item?.trim()),
                    weaknesses: aiEvaluatedDatas?.weaknesses?.filter((item) => item?.trim()),
                    evidence: aiEvaluatedDatas?.evidence?.filter((item) => item?.quote?.trim())
                }
            }

            const response = await api.post("SubmitInterviewFeedback", payload)

            if (response?.data?.status === "success") {
                showSnackbar("success", response?.data?.message || "Feedback Submitted Successfully")
                navigate("/interview-schedule-list")
            } else {
                showSnackbar("error", "Unable to submit the feedback")
            }
        } catch (err) {
            console.error("err", err)
            showSnackbar("error", "Failed to submit the feedback")
        } finally {
            setLoading(false)
            setOpenRemarksDialog(false)
        }
    }

    const handleSkillScoreChange = (newValue, index) => {
        setSkills((prev) => {
            const updatedScore = [...prev]

            updatedScore[index] = {
                ...prev[index],
                score: newValue
            }

            return updatedScore
        })
    }

    const handleSkillsChange = (e, index) => {
        setSkills((prev) => {
            const updatedFeedback = [...prev]

            updatedFeedback[index] = {
                ...prev[index],
                feedback: e.target.value
            }

            return updatedFeedback
        })
    }

    const handleStrengthsChange = (e, index) => {
        setAiEvaluatedDatas((prev) => {
            const updatedStrengths = [...(prev?.strengths || [])];

            updatedStrengths[index] = e.target.value;

            return {
                ...prev,
                strengths: updatedStrengths,
            };
        });
    }

    const handleWeaknessesChange = (e, index) => {
        setAiEvaluatedDatas((prev) => {
            const updatedWeaknesses = [...(prev?.weaknesses || [])]

            updatedWeaknesses[index] = e.target.value

            return {
                ...prev,
                weaknesses: updatedWeaknesses
            }
        })
    }

    const handleEvidenceChange = (e, index) => {
        setAiEvaluatedDatas((prev) => {
            const updatedEvidence = [...(prev?.evidence || [])]

            updatedEvidence[index] = {
                ...prev?.evidence[index],
                quote: e.target.value
            }

            return {
                ...prev,
                evidence: updatedEvidence
            }
        })
    }

    const handleNewSkillNameChange = (e, index) => {
        setSkills((prev) => {
            const updatedSkills = [...prev]

            updatedSkills[index] = {
                ...prev[index],
                skill_name: e.target.value
            }

            return updatedSkills;
        })
    }

    const handleAddSkills = () => {
        setSkills((prev) => [
            ...(prev || []),
            {
                skill_name: "",
                score: null,
                feedback: "",
                isNewSkill: true
            }
        ])

        setIsEditingSkills(true)
    }

    const handleAddStrengths = () => {
        setAiEvaluatedDatas((prev) => ({
            ...prev,
            strengths: [
                ...(prev?.strengths || []),
                ""
            ]
        }))

        setIsEditingStrengths(true)
    }

    const handleAddWeaknesses = () => {
        setAiEvaluatedDatas((prev) => ({
            ...prev,
            weaknesses: [
                ...(prev?.weaknesses || []),
                ""
            ]
        }))

        setIsEditingWeaknesses(true);
    }

    const handleAddEvidence = () => {
        setAiEvaluatedDatas((prev) => ({
            ...prev,
            evidence: [
                ...(prev?.evidence || []),
                {
                    type: "weakness",
                    source: "Transcript",
                    weakness: "",
                    quote: "",
                    timestamp: "",
                }
            ]
        }))
    }

    const handleOpenRejectDialog = () => {
        if (isInterviewAlreadyCompletedOrCancelled) {
            showSnackbar("error", `${interviewStatus || "This"} interview cannot be modified`)
            return;
        } else {
            setOpenDeleteDialog(true)
        }
    }

    const meetingUrl = (() => {
        const url = interviewDetails?.meeting_url;

        if (!url) return "";

        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }

        // if (url.startsWith("http//")) {
        //     return url.replace("http//", "http://");
        // }

        // if (url.startsWith("https//")) {
        //     return url.replace("https//", "https://");
        // }

        return `https://${url}`;
    })();

    return (
        <Box sx={{ minHeight: "100vh", p: 1 }}>
            {loading && (
                <OverAllLoader message={
                    "Submitting Feedback...Please wait"}
                />
            )}
            <CallOffInterviewDialog
                openDeleteDialog={openDeleteDialog}
                setOpenDeleteDialog={setOpenDeleteDialog}
                title="Can't take this interview?"
                desc="If you're unavailable due to an emergency or unexpected circumstance, you can notify the HR Manager or admin 
                      and the interview can be reassigned to someone else."
                btnTitle="Yes, I'm Not Available"
                interviewId={interviewId}
            />

            <FeedbackRemarksDialog
                openRemarksDialog={openRemarksDialog}
                setOpenRemarksDialog={setOpenRemarksDialog}
                handleSubmitFeedback={handleSubmitFeedback}
                loading={loading}
                setLoading={setLoading}
            />
            <Box>
                {/* Back Button */}
                <Grid
                    item
                    className="interview-feedback-header"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            window.history.back();
                        }
                    }}
                    onClick={() => window.history.back()}
                >
                    <ArrowBackIcon sx={{ fontSize: "17px" }} />
                    <Typography className="interview-feedback-back">
                        Back
                    </Typography>
                </Grid>

                {/* Header */}
                <Grid className="interview-feedback-schedules-header">
                    <Grid className="interview-feedback-schedules-header-left">
                        <Grid className="interview-feedback-schedules-icon">
                            <CalendarCheck2 />
                        </Grid>

                        <Grid>
                            <Typography className="interview-feedback-schedules-title">
                                Interview Schedules & Analysis
                            </Typography>
                        </Grid>
                    </Grid>

                    <IconButton
                        className="interview-feedback-not-available"
                        onClick={handleOpenRejectDialog}
                        disableTouchRipple
                    >
                        <AlertCircle size={16} />
                        <Typography className="interview-feedback-not-available-text">
                            Can't take this interview?
                        </Typography>
                    </IconButton>
                </Grid>

                {/* Interview Details */}
                <Grid className="interview-feedback-details-wrapper">

                    {/* Container 1 */}
                    <Box className="interview-feedback-details-card">
                        {/* Header */}
                        <Box className="interview-feedback-details-header">
                            <NotepadText
                                size={19}
                                color="#1c97e3"
                            />

                            <Typography className="interview-feedback-details-header-title">
                                Interview Details
                            </Typography>
                        </Box>

                        {/* Candidate Details */}
                        <Grid size={{ xs: 12, md: 6 }} container className="interview-feedback-details-content">
                            {/* Candidate */}
                            <Grid size={{ xs: 12, md: 2.4 }} className="interview-feedback-detail-item">
                                <User className="interview-feedback-detail-icon" />

                                <Box className="interview-feedback-detail-info">
                                    <Typography className="interview-feedback-detail-label">
                                        Candidate
                                    </Typography>

                                    <Typography className="interview-feedback-detail-value">
                                        {interviewDetails?.candidate?.first_name || "N/A"} {interviewDetails?.candidate?.last_name}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Job Role */}
                            <Grid size={{ xs: 12, md: 2.4 }} className="interview-feedback-detail-item">
                                <Building2 className="interview-feedback-detail-icon" />

                                <Box className="interview-feedback-detail-info">
                                    <Typography className="interview-feedback-detail-label">
                                        Job Role
                                    </Typography>

                                    <Typography className="interview-feedback-detail-value">
                                        {interviewDetails?.program_name || "N/A"}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Interview Date */}
                            <Grid size={{ xs: 12, md: 2.4 }} className="interview-feedback-detail-item">
                                <CalendarCheck2 className="interview-feedback-detail-icon" />

                                <Box className="interview-feedback-detail-info">
                                    <Typography className="interview-feedback-detail-label">
                                        Interview Date
                                    </Typography>

                                    <Typography className="interview-feedback-detail-value">
                                        {interviewDetails?.scheduled_at ? dayjs(interviewDetails?.scheduled_at)?.format('DD-MM-YYYY') : "-"}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Duration */}
                            <Grid size={{ xs: 12, md: 2.4 }} className="interview-feedback-detail-item">
                                <TimerIcon className="interview-feedback-detail-icon" />

                                <Box className="interview-feedback-detail-info">
                                    <Typography className="interview-feedback-detail-label">
                                        Duration
                                    </Typography>

                                    <Typography className="interview-feedback-detail-value">
                                        {`${interviewDetails?.duration_minutes || 0} Minutes` || "N/A"}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Status */}
                            <Grid size={{ xs: 12, md: 2.4 }} className="interview-feedback-detail-item">
                                <ChartArea className="interview-feedback-detail-icon" />

                                <Box className="interview-feedback-detail-info">
                                    <Typography className="interview-feedback-detail-label">
                                        Status
                                    </Typography>

                                    <Typography className="interview-feedback-detail-value">
                                        {interviewDetails?.status || "N/A"}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Meeting Link */}
                            <Grid
                                size={{ xs: 12, md: 12 }}
                                className="interview-feedback-meeting-link"
                            >
                                <Box>
                                    <Typography className="interview-feedback-meeting-link-title">
                                        Interview Meeting Link
                                    </Typography>

                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        // href={meetingUrl || "#"}
                                        className="interview-feedback-meeting-link-url"
                                    >
                                        {interviewDetails?.meeting_url || "N/A"}
                                    </a>
                                </Box>

                                <Box>
                                    <Button
                                        className="interview-feedback-join-meeting"
                                        endIcon={<LucideArrowUpRightFromSquare size={15} />}
                                        variant="contained"
                                        onClick={() => {
                                            if (isInterviewAlreadyCompletedOrCancelled) {
                                                showSnackbar("error", `${interviewStatus || "This"} interview cannot be modified`)
                                                return;
                                            }

                                            if (meetingUrl) {
                                                window.open(meetingUrl, "_blank", "noopener,noreferrer");
                                            }
                                        }}
                                    >
                                        Join Meeting
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Run Analysis Container / Container 2*/}
                    <Grid className="interview-feedback-candidate-card">
                        {/* Candidate Header */}
                        <Box className="interview-feedback-candidate-header">
                            <Avatar className="interview-feedback-candidate-avatar">
                                {interviewDetails?.candidate?.first_name?.slice(0, 1) || undefined}
                            </Avatar>

                            <Box className="interview-feedback-candidate-info">
                                <Box className="interview-feedback-candidate-name-row">
                                    <Typography className="interview-feedback-candidate-name">
                                        {interviewDetails?.candidate?.first_name || "N/A"} {interviewDetails?.candidate?.last_name}
                                    </Typography>

                                    <Typography className="interview-feedback-candidate-badge">
                                        SRC · JD + RESUME + TRANSCRIPT
                                    </Typography>
                                </Box>

                                <Typography className="interview-feedback-candidate-meta">
                                    {interviewDetails?.program_name || "N/A"} · Technical Interview · {interviewDetails?.scheduled_at ? dayjs(interviewDetails?.scheduled_at)?.format('DD-MM-YYYY') : "-"}
                                </Typography>

                            </Box>
                        </Box>

                        {/* Document Details */}
                        <Grid className="interview-feedback-document-list">
                            <Box className="interview-feedback-document-item">
                                <CheckCircleRounded className="interview-feedback-document-icon" />

                                <Typography className="interview-feedback-document-item-text">
                                    JD · {interviewDetails?.program_name || "N/A"}
                                </Typography>
                            </Box>

                            <Box className="interview-feedback-document-item">
                                <CheckCircleRounded className="interview-feedback-document-icon" />

                                <Typography className="interview-feedback-document-item-text">
                                    Resume · {`${interviewDetails?.candidate?.first_name}_Resume` || "N/A"}.pdf
                                </Typography>
                            </Box>

                            <Box className="interview-feedback-document-item-transcript">
                                {resumeFile?.name ? (
                                    <CheckCircleRounded className="interview-feedback-uploaded-document-icon" />
                                ) : (
                                    <Warning className="interview-feedback-transcript-warning-icon" />
                                )}

                                <Typography className="interview-feedback-document-transcript-text">
                                    Transcript · {`${resumeFile?.name ? resumeFile?.name : 'No File Uploaded'}`}
                                </Typography>
                            </Box>

                        </Grid>

                        {/* Divider */}
                        <div className="interview-feedback-divider" />

                        {/* File Upload */}
                        <Grid className="interview-feedback-file-upload">
                            <Box className="interview-feedback-file-upload-box">
                                <Box className="interview-feedback-file-upload-icon">
                                    <NotepadText
                                        size={19}
                                        color="#1c97e3"
                                    />
                                </Box>

                                <Box className="interview-feedback-file-upload-content">
                                    <Box>
                                        <Typography className="interview-feedback-file-upload-title">
                                            Upload transcript file
                                        </Typography>

                                        <Typography className="interview-feedback-file-upload-description">
                                            Drag and drop or click to browse
                                        </Typography>
                                    </Box>

                                    <Box
                                        component="label"
                                        htmlFor="interview-feedback-transcript-upload"
                                        className="interview-feedback-file-upload-button"
                                    >
                                        <UploadCloudIcon
                                            size={20}
                                            color="#000"
                                        />
                                        <Typography className="interview-feedback-file-upload-button-text">
                                            Upload
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Analysis Footer */}
                            <Box className="interview-feedback-analysis-footer">
                                <Box className="interview-feedback-analysis-info">
                                    <LucideSparkles color="#1c97e3" size={17} />

                                    <Typography className="interview-feedback-analysis-text">
                                        AI compares every source and cites the strongest evidence.
                                    </Typography>
                                </Box>

                                <Button
                                    startIcon={isFileUploading ?
                                        <CircularProgress color="inherit" size={15} /> :
                                        <Play size={17}
                                        />
                                    }
                                    variant="contained"
                                    onClick={handleRunAnalysis}
                                    disabled={isFileUploading}
                                >
                                    {isFileUploading ? "Analysing..." : "Run Analysis"}
                                </Button>
                            </Box>

                            {/* Hidden File Input */}
                            <input
                                id="interview-feedback-transcript-upload"
                                type="file"
                                hidden
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];

                                    if (file) {
                                        setResumeFile(file);
                                    }

                                    e.target.value = "";
                                }}
                                onClick={(e) => {
                                    if (isInterviewAlreadyCompletedOrCancelled) {
                                        e.preventDefault();
                                        showSnackbar("error", `${interviewStatus || "This"} interview cannot be modified`);
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Container 3 */}
                <Grid
                    container
                    className="interview-feedback-verdict-wrapper"
                    spacing={2}
                >
                    {/* Verdict */}
                    <Grid size={{ xs: 12, md: 3.5 }}>
                        <Grid
                            className="interview-feedback-verdict-card"
                        >
                            <Box className="interview-feedback-verdict-header">
                                <Typography className="interview-feedback-verdict-label">
                                    (a) Verdict
                                </Typography>

                                {isFileUploading ? (
                                    <Skeleton
                                        variant="text"
                                        width={85}
                                        height={20}
                                    />
                                ) : (
                                    <Typography className="interview-feedback-verdict-label">
                                        Match {`${aiEvaluatedDatas?.match_percentage || 0}%`}
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <Box className="interview-feedback-verdict-score-wrapper">
                                    {isFileUploading ? (
                                        <Skeleton
                                            variant="text"
                                            width={100}
                                            height={80}
                                        />
                                    ) : (
                                        <>
                                            <Typography className="interview-feedback-verdict-score">
                                                {aiEvaluatedDatas?.overall_score || 0}
                                            </Typography>

                                            <Typography className="interview-feedback-verdict-score-total">
                                                / 100
                                            </Typography>
                                        </>
                                    )}
                                </Box>

                                {isFileUploading ? (
                                    <Skeleton
                                        variant="rounded"
                                        width={300}
                                        height={16}
                                    />
                                ) : (
                                    <Slider
                                        value={aiEvaluatedDatas?.overall_score || 0}
                                        onChange={(event, newValue) => {
                                            setAiEvaluatedDatas((prev) => ({
                                                ...prev,
                                                overall_score: newValue,
                                            }))
                                        }}
                                        aria-label="Default"
                                        valueLabelDisplay="auto"
                                        className="interview-feedback-verdict-slider"
                                        min={0}
                                        max={100}
                                        step={1}
                                    />
                                )}
                            </Box>

                            {isFileUploading ? (
                                <Box className="interview-feedback-verdict-recommendation">
                                    <Box className="interview-feedback-verdict-recommendation-header">
                                        <Skeleton
                                            variant="circular"
                                            width={10}
                                            height={10}
                                        />

                                        <Skeleton
                                            variant="text"
                                            width={115}
                                            height={22}
                                        />
                                    </Box>

                                    <Box className="interview-feedback-recommendation-skeleton">
                                        <Skeleton variant="text" width="95%" height={20} />
                                        <Skeleton variant="text" width="80%" height={20} />
                                    </Box>

                                    <Box className="interview-feedback-edit-skeleton">
                                        <Skeleton variant="rounded" width={55} height={26} />
                                    </Box>
                                </Box>
                            ) : (
                                <Box className="interview-feedback-verdict-recommendation">
                                    <Box className="interview-feedback-verdict-recommendation-header">
                                        <div className="interview-feedback-verdict-recommendation-dot" />

                                        <Typography className="interview-feedback-verdict-recommendation-label">
                                            Recommendation
                                        </Typography>
                                    </Box>

                                    {isEditingRecommendation ? (
                                        <Box className="interview-feedback-recommendation-edit-wrapper">
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                sx={{
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "13px",
                                                    },
                                                }}
                                                value={aiEvaluatedDatas?.recommendation || ""}
                                                onChange={(e) => {
                                                    setAiEvaluatedDatas((prev) => ({
                                                        ...prev,
                                                        recommendation: e.target.value,
                                                    }));
                                                }}
                                            />

                                            <Box
                                                className="interview-feedback-save"
                                                onClick={() => {
                                                    setIsEditingRecommendation(false);
                                                }}
                                            >
                                                <CheckIcon size={18} />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <>
                                            <Typography className="interview-feedback-verdict-recommendation-description">
                                                {aiEvaluatedDatas?.recommendation || "N/A"}
                                            </Typography>

                                            <Box
                                                className="interview-feedback-edit"
                                                onClick={() => {
                                                    setIsEditingRecommendation(true);
                                                }}
                                            >
                                                <FileEditIcon color="grey" size={17} />
                                                <Typography className="interview-feedback-edit-text">
                                                    Edit
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Grid>

                        {isFileUploading && (
                            <Box className="interview-feedback-summary">
                                <Box className="interview-feedback-verdict-recommendation-header">
                                    <Skeleton
                                        variant="circular"
                                        width={10}
                                        height={10}
                                    />

                                    <Skeleton
                                        variant="text"
                                        width={115}
                                        height={22}
                                    />
                                </Box>

                                <Box className="interview-feedback-recommendation-skeleton">
                                    <Skeleton variant="text" width="95%" height={20} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </Box>
                            </Box>
                        )}

                        {aiEvaluatedDatas?.summary && (
                            <Grid className="interview-feedback-summary">
                                <Box className="interview-feedback-verdict-recommendation-header">
                                    <div className="interview-feedback-verdict-summary-dot" />

                                    <Typography className="interview-feedback-verdict-summary-label">
                                        Summary
                                    </Typography>
                                </Box>

                                {isEditingSummary ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            sx={{
                                                "& .MuiInputBase-input": {
                                                    fontSize: "13px",
                                                },
                                            }}
                                            value={aiEvaluatedDatas?.summary}
                                            onChange={(e) => {
                                                setAiEvaluatedDatas((prev) => ({
                                                    ...prev,
                                                    summary: e.target.value
                                                }))
                                            }}
                                        />

                                        <Box
                                            className="interview-feedback-save"
                                            onClick={() => {
                                                setIsEditingSummary(false);
                                            }}
                                        >
                                            <CheckIcon size={18} />
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Typography className="interview-feedback-verdict-recommendation-description">
                                            {aiEvaluatedDatas?.summary || "N/A"}
                                        </Typography>

                                        <Box
                                            className="interview-feedback-edit"
                                            onClick={() => {
                                                setIsEditingSummary(true);
                                            }}
                                        >
                                            <FileEditIcon color="grey" size={17} />
                                            <Typography className="interview-feedback-edit-text">
                                                Edit
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        )}
                    </Grid>
                    {/* Skill Scores */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        {/* 1 */}
                        <Grid className="interview-feedback-skill-scores-card">
                            <Box className="interview-feedback-verdict-header">
                                <Typography className="interview-feedback-verdict-label">
                                    (b) Skill Scores
                                </Typography>

                                {isInterviewSkillsExtracting ? (
                                    <Skeleton
                                        variant="text"
                                        height={20}
                                        width={50}
                                    />
                                ) : (
                                    <Typography className="interview-feedback-verdict-label">
                                        {skills?.length ? `${skills?.length} ${skills?.length > 1 ? 'Skills' : 'Skill'}` : '0 Skills'}
                                    </Typography>
                                )}
                            </Box>

                            <Grid
                                container
                                spacing={2}
                                className="interview-feedback-skill-scores-list"
                            >
                                {isInterviewSkillsExtracting ? (
                                    Array.from({ length: 2 }).map((_, index) => (
                                        <Grid
                                            key={index}
                                            size={{ xs: 12, md: 6 }}
                                            className="interview-feedback-skill-card"
                                        >
                                            <Box className="interview-feedback-skill-header">
                                                <Skeleton
                                                    variant="text"
                                                    width="40%"
                                                    height={20}
                                                />

                                                <Skeleton
                                                    variant="text"
                                                    width="15%"
                                                    height={20}
                                                />
                                            </Box>

                                            <Skeleton
                                                variant="rounded"
                                                width="100%"
                                                height={6}
                                                className="interview-feedback-skill-slider-skeleton"
                                            />

                                            <Skeleton
                                                variant="text"
                                                width="90%"
                                                height={18}
                                            />
                                        </Grid>
                                    ))
                                ) : (
                                    skills?.map((item, index) => {
                                        return (
                                            <Grid
                                                key={index}
                                                size={{ xs: 12, md: 6 }}
                                                className="interview-feedback-skill-card"
                                            >
                                                <Box className="interview-feedback-skill-header">
                                                    {item?.isNewSkill ? (
                                                        <TextField
                                                            value={item?.skill_name}
                                                            onChange={(e) => handleNewSkillNameChange(e, index)}
                                                            sx={{
                                                                width: '100px',
                                                                "& .MuiInputBase-root": {
                                                                    height: "25px",
                                                                },
                                                                "& .MuiInputBase-input": {
                                                                    fontSize: "12px",
                                                                    padding: "4px 8px",
                                                                },
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography className="interview-feedback-skill-name">
                                                            {item?.skill_name || "N/A"}
                                                        </Typography>
                                                    )}

                                                    <Typography className="interview-feedback-skill-score">
                                                        {item?.score ?? 0}/10
                                                    </Typography>
                                                </Box>

                                                {
                                                    isFileUploading ? (
                                                        <Skeleton
                                                            variant="rounded"
                                                            width="100%"
                                                            height={6}
                                                            className="interview-feedback-skill-slider-skeleton"
                                                        />
                                                    ) : (
                                                        <Slider
                                                            value={item?.score || 0}
                                                            onChange={(e, newValue) => handleSkillScoreChange(newValue, index)}
                                                            aria-label="Default"
                                                            valueLabelDisplay="auto"
                                                            size="small"
                                                            min={0}
                                                            max={10}
                                                            step={1}
                                                        />
                                                    )
                                                }

                                                {
                                                    isEditingSkills ? (
                                                        <Box className="interview-feedback-skill-edit-wrapper">
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                minRows={1}
                                                                value={item?.feedback || ""}
                                                                onChange={(e) => handleSkillsChange(e, index)}
                                                                sx={{
                                                                    "& .MuiInputBase-input": {
                                                                        fontSize: "13px",
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        < Typography className="interview-feedback-skill-description">
                                                            {!isFileUploading ? (
                                                                item?.feedback || "N/A"
                                                            ) : (
                                                                <Skeleton
                                                                    variant="text"
                                                                    width="90%"
                                                                    height={18}
                                                                />
                                                            )}
                                                        </Typography>
                                                    )
                                                }
                                            </Grid>
                                        );
                                    })
                                )}
                            </Grid>

                            {isEditingSkills ? (
                                <Box
                                    className="interview-feedback-save"
                                    onClick={() => {
                                        setIsEditingSkills(null);
                                    }}
                                >
                                    <CheckIcon size={18} />
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        className="interview-feedback-edit"
                                        onClick={() => {
                                            skills?.length ? setIsEditingSkills(true) : showSnackbar("error", "No Skills are Available")
                                        }}
                                    >
                                        <FileEditIcon color="grey" size={17} />

                                        <Typography className="interview-feedback-edit-text">
                                            Edit
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{ ml: 1 }}
                                        className="interview-feedback-edit"
                                        onClick={handleAddSkills}
                                    >
                                        <PlusIcon color="grey" size={17} />

                                        <Typography className="interview-feedback-edit-text">
                                            Add
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Grid>

                        {/* 2 */}
                        {isFileUploading ? (
                            <Grid className="interview-feedback-strength">
                                <Box className="interview-feedback-strength-header">
                                    <Skeleton
                                        variant="circular"
                                        width={10}
                                        height={10}
                                    />

                                    <Skeleton
                                        variant="text"
                                        width={75}
                                        height={22}
                                    />
                                </Box>

                                <Box className="interview-feedback-strengths">
                                    <Skeleton variant="text" width="95%" height={20} />
                                    <Skeleton variant="text" width="85%" height={20} />
                                    <Skeleton variant="text" width="75%" height={20} />
                                </Box>

                                <Box className="interview-feedback-edit-skeleton">
                                    <Skeleton variant="rounded" width={55} height={26} />
                                </Box>
                            </Grid>
                        ) : (
                            <Grid className="interview-feedback-strength">
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <div className="interview-feedback-verdict-strength-dot" />

                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            color: '#00A800',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Strengths
                                    </Typography>
                                </Box>

                                <Box sx={{ fontSize: "14px", color: "grey", mt: 1 }}>
                                    {isEditingStrengths ? (
                                        <Box className="interview-feedback-strengths">
                                            <ol>
                                                {aiEvaluatedDatas.strengths.map((item, index) => (
                                                    <li key={index}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={1}
                                                            value={item}
                                                            onChange={(e) =>
                                                                handleStrengthsChange(e, index)
                                                            }
                                                            sx={{
                                                                "& .MuiInputBase-input": {
                                                                    fontSize: "13px",
                                                                },
                                                            }}
                                                        />
                                                    </li>
                                                ))}
                                            </ol>
                                        </Box>
                                    ) : (
                                        <>
                                            {!aiEvaluatedDatas?.strengths?.length ? (
                                                <Typography className="interview-feedback-verdict-recommendation-description">
                                                    N/A
                                                </Typography>
                                            ) : (
                                                <Box className="interview-feedback-strengths">
                                                    <ol>
                                                        {aiEvaluatedDatas.strengths.map((item, index) => (
                                                            <li key={index}>{item}</li>
                                                        ))}
                                                    </ol>
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    {isEditingStrengths ? (
                                        <Box
                                            className="interview-feedback-save"
                                            onClick={() => setIsEditingStrengths(false)}
                                        >
                                            <CheckIcon size={18} />
                                        </Box>
                                    ) : (
                                        <>
                                            <Box
                                                className="interview-feedback-edit"
                                                onClick={() => {
                                                    setAiEvaluatedDatas((prev) => ({
                                                        ...prev,
                                                        strengths: prev?.strengths?.length
                                                            ? prev?.strengths
                                                            : [""]
                                                    }))

                                                    setIsEditingStrengths(true)
                                                }}
                                            >
                                                <FileEditIcon color="grey" size={17} />

                                                <Typography className="interview-feedback-edit-text">
                                                    Edit
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{ ml: 1 }}
                                                className="interview-feedback-edit"
                                                onClick={handleAddStrengths}
                                            >
                                                <PlusIcon color="grey" size={17} />

                                                <Typography className="interview-feedback-edit-text">
                                                    Add
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {/* 3 */}
                        {isFileUploading ? (
                            <Grid className="interview-feedback-concerns">
                                <Box className="interview-feedback-concerns-header">
                                    <Skeleton
                                        variant="circular"
                                        width={10}
                                        height={10}
                                    />

                                    <Skeleton
                                        variant="text"
                                        width={80}
                                        height={22}
                                    />
                                </Box>

                                <Box className="interview-feedback-strengths">
                                    <Skeleton variant="text" width="95%" height={20} />
                                    <Skeleton variant="text" width="85%" height={20} />
                                    <Skeleton variant="text" width="75%" height={20} />
                                </Box>

                                <Box className="interview-feedback-edit-skeleton">
                                    <Skeleton variant="rounded" width={55} height={26} />
                                </Box>
                            </Grid>
                        ) : (
                            <Grid className="interview-feedback-concerns">
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <div className="interview-feedback-verdict-concern-dot" />

                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            color: '#ED1C24',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        CONCERNS
                                    </Typography>
                                </Box>

                                <Box sx={{ fontSize: '14px', color: 'grey', mt: 1 }}>
                                    {isEditingWeaknesses ? (
                                        <Box className="interview-feedback-strengths">
                                            <ol>
                                                {aiEvaluatedDatas?.weaknesses?.map((item, index) => (
                                                    <li key={index}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={1}
                                                            value={item}
                                                            onChange={(e) =>
                                                                handleWeaknessesChange(e, index)
                                                            }
                                                            sx={{
                                                                "& .MuiInputBase-input": {
                                                                    fontSize: "13px",
                                                                },
                                                            }}
                                                        />
                                                    </li>
                                                ))}
                                            </ol>
                                        </Box>
                                    ) : (
                                        <>
                                            {!aiEvaluatedDatas?.weaknesses?.length ? (
                                                <Typography className="interview-feedback-verdict-recommendation-description">
                                                    N/A
                                                </Typography>
                                            ) : (
                                                <Box className="interview-feedback-strengths">
                                                    <ol>
                                                        {aiEvaluatedDatas.weaknesses.map((item, index) => (
                                                            <li key={index}>{item}</li>
                                                        ))}
                                                    </ol>
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    {isEditingWeaknesses ? (
                                        <Box
                                            className="interview-feedback-save"
                                            onClick={() => setIsEditingWeaknesses(false)}
                                        >
                                            <CheckIcon size={18} />
                                        </Box>
                                    ) : (
                                        <>
                                            <Box
                                                className="interview-feedback-edit"
                                                onClick={() => {
                                                    setAiEvaluatedDatas((prev) => ({
                                                        ...prev,
                                                        weaknesses: prev?.weaknesses?.length
                                                            ? prev.weaknesses
                                                            : [""],
                                                    }));

                                                    setIsEditingWeaknesses(true);
                                                }}

                                            >
                                                <FileEditIcon color="grey" size={17} />

                                                <Typography className="interview-feedback-edit-text">
                                                    Edit
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{ ml: 1 }}
                                                className="interview-feedback-edit"
                                                onClick={handleAddWeaknesses}
                                            >
                                                <PlusIcon color="grey" size={17} />

                                                <Typography className="interview-feedback-edit-text">
                                                    Add
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    {/* Evidence */}
                    <Grid
                        size={{ xs: 12, md: 3.5 }}
                        className="interview-feedback-evidence-card"
                    >
                        <Box className="interview-feedback-verdict-header">
                            <Typography className="interview-feedback-verdict-label">
                                (c) Evidence
                            </Typography>
                        </Box>

                        {isFileUploading ? (
                            Array.from({ length: 3 }).map((_, index) => (
                                <Box
                                    key={index}
                                    className="interview-feedback-evidence-item"
                                >
                                    <Box className="interview-feedback-evidence-meta">
                                        <Skeleton
                                            variant="rounded"
                                            width={65}
                                            height={24}
                                        />

                                        <Skeleton
                                            variant="text"
                                            width={75}
                                            height={20}
                                        />
                                    </Box>

                                    <Box className="interview-feedback-evidence-text-skeleton">
                                        <Skeleton
                                            variant="text"
                                            width="95%"
                                            height={20}
                                        />

                                        <Skeleton
                                            variant="text"
                                            width="80%"
                                            height={20}
                                        />
                                    </Box>

                                    <Box className="interview-feedback-edit-skeleton">
                                        <Skeleton
                                            variant="rounded"
                                            width={55}
                                            height={26}
                                        />
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            isEditingEvidences ? (
                                <Box className="interview-feedback-strengths">
                                    {aiEvaluatedDatas?.evidence?.map((item, index) => (
                                        <Box
                                            key={index}
                                            className="interview-feedback-evidence-item"
                                        >
                                            <Box className="interview-feedback-evidence-meta">
                                                <Typography
                                                    className={
                                                        item?.type === "weakness"
                                                            ? "interview-feedback-evidence-concern"
                                                            : "interview-feedback-evidence-strength"
                                                    }
                                                >
                                                    {item?.type || "N/A"}
                                                </Typography>

                                                <Typography className="interview-feedback-evidence-source">
                                                    · Transcript
                                                </Typography>
                                            </Box>

                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={1}
                                                value={item?.quote}
                                                onChange={(e) => handleEvidenceChange(e, index)}
                                                sx={{
                                                    mt: 1,
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "13px",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                aiEvaluatedDatas?.evidence?.length > 0 ? (
                                    aiEvaluatedDatas?.evidence?.map((item, index) => (
                                        <Box
                                            key={index}
                                            className="interview-feedback-evidence-item"
                                        >
                                            <Box className="interview-feedback-evidence-meta">
                                                <Typography
                                                    className={
                                                        item?.type === "weakness"
                                                            ? "interview-feedback-evidence-concern"
                                                            : "interview-feedback-evidence-strength"
                                                    }
                                                >
                                                    {item?.type || "N/A"}
                                                </Typography>

                                                <Typography className="interview-feedback-evidence-source">
                                                    · Transcript
                                                </Typography>
                                            </Box>

                                            <Typography className="interview-feedback-evidence-text">
                                                {item?.quote || "N/A"}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography className="interview-feedback-verdict-recommendation-description">
                                        N/A
                                    </Typography>
                                )
                            )
                        )}

                        {isEditingEvidences ? (
                            <Box
                                className="interview-feedback-save"
                                onClick={() => setIsEditingEvidences(false)}
                            >
                                <CheckIcon size={18} />
                            </Box>
                        ) : !isFileUploading ? (
                            <>
                                <Box
                                    className="interview-feedback-edit"
                                    onClick={!aiEvaluatedDatas?.evidence?.length ? () => showSnackbar("error", "Add atlease 1 row before editing") : () => setIsEditingEvidences(true)}
                                >
                                    <FileEditIcon color="grey" size={17} />

                                    <Typography className="interview-feedback-edit-text">
                                        Edit
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{ ml: 1 }}
                                    className="interview-feedback-edit"
                                    onClick={handleAddEvidence}
                                >
                                    <PlusIcon color="grey" size={17} />

                                    <Typography className="interview-feedback-edit-text">
                                        Add
                                    </Typography>
                                </Box>
                            </>
                        ) : <></>
                        }
                    </Grid>
                </Grid>

                <Grid sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        startIcon={loading ?
                            <CircularProgress color="inherit" size={15} /> :
                            <Save size={17}
                            />
                        }
                        variant="contained"
                        onClick={() => {
                            if (isInterviewAlreadyCompletedOrCancelled) {
                                showSnackbar("error", `${interviewStatus || "This"} interview cannot be modified`);
                                return;
                            }
                            setOpenRemarksDialog(true)
                        }}
                    >
                        {"Save"}
                    </Button>
                </Grid>
            </Box>
        </Box >
    );
};

export default InterviewFeedback;
